/**
 * Guest selector (CHANGE 2026-10-18 §2.2): adult and child steppers, one age selector per child, and the
 * capacity hint from the server (/config party.hints, looked up by party size — no rule here). Elements are
 * built once and updated, so focus stays on the button being pressed.
 *
 * Children row (date-picker change, §11): label « Enfants / 0 à 17 ans » | the age selectors | the stepper, in
 * one grid row; under ~560 px the ages drop to a full-width line. Each select: no visible label (aria-label
 * « Âge de l'enfant n »), a disabled « Âge » placeholder, required — no default age, no search while one is
 * missing (booking.canSearch()).
 */
import { h, clear } from './dom.js';

export function createGuests({ booking, i18n, msg, compact = false }) {
	const t = i18n.t;
	const state = booking.state;

	function stepper(kind, onChange, sub = null, middle = null) {
		const name = h('span', { class: 'pap-stepper-name' });
		const label = h('span', { class: 'pap-stepper-l', id: 'pap-' + kind + '-' + uid }, name, sub);
		const minus = h('button', { type: 'button', class: 'pap-step-btn', on: { click: () => onChange(-1) } }, '−');
		const out = h('output', { class: 'pap-num', 'aria-labelledby': label.id });
		const plus = h('button', { type: 'button', class: 'pap-step-btn', on: { click: () => onChange(1) } }, '+');
		return { el: h('div', { class: 'pap-stepper pap-stepper--' + kind }, label, middle, minus, out, plus), label: name, minus, out, plus };
	}

	const uid = Math.random().toString(36).slice(2, 7);
	// The oldest child age is the Règles setting sent by /start (1.4.0): none built in; nothing shown before /start.
	const maxAge = () => state.config?.party?.maxChildAge;
	const childrenSub = h('small', { class: 'pap-stepper-sub' });
	const adultsSub = h('small', { class: 'pap-stepper-sub' });
	const ages = h('div', { class: 'pap-ages' });
	const adults = stepper('adults', (d) => booking.setAdults(state.adults + d), adultsSub);
	const children = stepper('children', (d) => booking.setChildren(state.childrenAges.length + d), childrenSub, ages);
	const hint = h('p', { class: 'pap-party-hint', 'aria-live': 'polite' });
	const el = h('fieldset', { class: 'pap-guests' + (compact ? ' pap-guests--compact' : '') },
		h('legend', {}, t('ui.guestsLegend')), adults.el, children.el, hint);
	let agesCount = -1;
	let agesMax;

	function updateStepper(s, key, value, min, max) {
		const count = value === 0 ? 2 : value; // plural label with 0 too (« 0 enfants »)
		clear(s.label).append(t(key, [], count));
		clear(s.out).append(i18n.value(String(value)));
		s.minus.disabled = value <= min;
		s.plus.disabled = value >= max;
		s.minus.setAttribute('aria-label', i18n.s(key === 'ui.adults' ? 'ui.decreaseAdults' : 'ui.decreaseChildren'));
		s.plus.setAttribute('aria-label', i18n.s(key === 'ui.adults' ? 'ui.increaseAdults' : 'ui.increaseChildren'));
	}

	function renderAges() {
		clear(ages);
		const oldest = maxAge() ?? -1;
		state.childrenAges.forEach((age, i) => {
			ages.append(h('select', {
				class: 'pap-age',
				required: true,
				'aria-label': i18n.s('ui.childAge', [i + 1]),
				on: { change: (e) => booking.setAge(i, e.target.value === '' ? null : Number(e.target.value)) }
			},
			h('option', { value: '', disabled: true, selected: age === null }, i18n.s('ui.agePlaceholder')),
			...Array.from({ length: oldest + 1 }, (_, a) => h('option', { value: String(a), selected: age === a }, i18n.s('ui.age', [a], a)))));
		});
		ages.hidden = state.childrenAges.length === 0;
		agesCount = state.childrenAges.length;
		agesMax = maxAge();
	}

	function update() {
		const max = booking.maxGuests();
		updateStepper(adults, 'ui.adults', state.adults, 1, max - state.childrenAges.length);
		updateStepper(children, 'ui.children', state.childrenAges.length, 0, max - state.adults);
		clear(childrenSub);
		clear(adultsSub);
		if (Number.isInteger(maxAge())) {
			childrenSub.append(t('ui.childrenAges', [0, maxAge()], maxAge()));
			adultsSub.append(t('ui.adultsAges', [maxAge() + 1], maxAge() + 1));
		}
		if (agesCount !== state.childrenAges.length || agesMax !== maxAge()) {
			renderAges();
		}
		const party = booking.guests();
		const hints = state.config?.party?.hints;
		clear(hint);
		if (hints) {
			const node = msg.partyHint(hints[party] ?? hints[String(party)]);
			if (node) {
				hint.append(node);
			}
			if (party >= state.config.party.maxGuests) {
				hint.append(i18n.sep(' '), t('party.MORE', [state.config.party.maxGuests]));
			}
		}
	}

	update();
	return { el, update };
}
